import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { apiClientDrawerV2 as apiClient } from '@lift-admin/utils';
import {
  buildDetails,
  buildSummary,
  diffFields,
  type FieldSpec,
} from '@lift-admin/utils/logChangeFormatter';
import { Info } from 'lucide-react';
import { Toastify } from 'lift-ui';
import { actionLog } from '../../../App';
import {
  Button,
  DataTable,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Input,
  Label,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';

const theme = createTheme({
  palette: {
    primary: {
      main: '#000',
    },
    secondary: {
      main: 'rgba(0, 0, 0, 0)',
    },
  },
  typography: {
    fontFamily: '"Figtree", sans-serif',
  },
});

interface ItemsProps {
  toggle: string;
  searchText: string;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const UI_COMPONENT_FIELD_SPECS: FieldSpec[] = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
];

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const UIComponents = ({
  toggle,
  searchText,
  openDialog,
  setOpenDialog,
  hasEditAccess = true,
  hasManageAccess = true,
}: ItemsProps) => {
  const [loadData, setLoadData] = useState<boolean>(false);
  const [drawerItem, setDrawerItems] = useState<any>([]);
  const [drawerItemBackUp, setDrawerItemsBackup] = useState<any>([]);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const [currentId, setCurrentId] = useState<string | number | null>(null);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const editOriginalRef = useRef<{ name?: string; description?: string }>({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const getCustomComponents = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClient.get('/admin/ui-components', {
        params: {
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      const uiComponents = res?.data?.sort(
        (
          a: { created_at: string | number | Date },
          b: { created_at: string | number | Date }
        ) => {
          return (
            (new Date(b.created_at) as any) - (new Date(a.created_at) as any)
          );
        }
      );
      setDrawerItems(uiComponents);
      setDrawerItemsBackup(uiComponents);
    } catch {
      Toastify('error', 'UI Components not fetched');
    } finally {
      setLoadData(false);
    }
  };

  useEffect(() => {
    getCustomComponents();
  }, []);

  const rowData = useMemo(() => drawerItem, [drawerItem]);

  // Pagination calculations
  const paginatedData = useMemo(() => {
    return rowData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [rowData, page, rowsPerPage]);

  const totalPages = Math.ceil(rowData.length / rowsPerPage);

  const searchResults = (query: any) => {
    const filteredUsers = [...drawerItemBackUp];
    let results = [];
    for (let i = 0; i < filteredUsers.length; i++) {
      if (filteredUsers[i]['name']?.toLowerCase().includes(query.toLowerCase()))
        results.push(filteredUsers[i]);
    }

    setDrawerItems(results);
    setPage(0); // Reset to first page on search
  };

  useEffect(() => {
    searchResults(searchText);
  }, [searchText]);

  const resolveComponentName = (id: any) =>
    drawerItemBackUp?.find((c: any) => String(c.id) === String(id))?.name ||
    name ||
    undefined;

  const handleDialogClose = () => {
    setOpenDialog(false);
    setName('');
    setDescription('');
  };

  const handleAddChannel = async () => {
    try {
      if (!name || name.length === 0) {
        Toastify('error', 'Please enter correct fields');
        return;
      }

      const payload = {
        name,
        description,
      };

      const res = await apiClient.post(
        '/drawer/v1/admin/drawer/structure/ui-component',
        payload
      );

      getCustomComponents();
      setName('');
      setDescription('');
      setOpenDialog(false);
      Toastify('success', 'UI Component added successfully');

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const desc = {
        message: buildSummary('UI component', name, 'created'),
        status: res.status,
        method: res.config?.method,
        url: res.config?.url,
        category: 'drawer',
        action: 'create_ui_component',
        ui_component_id: res?.data?.id ?? res?.data?.response?.id,
        ui_component_name: name,
        user_id: user?.id,
        user_name: user?.name,
        details: buildDetails(payload, UI_COMPONENT_FIELD_SPECS, {
          skipEmpty: true,
        }),
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      setOpenDialog(false);
      Toastify('error', 'UI Component not added');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to create UI component "${name}"`,
          status: err?.response?.status || 500,
          method: 'POST',
          url: '/drawer/v1/admin/drawer/structure/ui-component',
          category: 'drawer',
          action: 'create_ui_component',
          ui_component_name: name,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleDelete = async (id: any) => {
    const componentName = resolveComponentName(id);
    try {
      if (!id || isNaN(Number(id))) {
        Toastify(
          'error',
          'Invalid UI Component ID. Please select correct component'
        );
        return;
      }
      const res = await apiClient.delete(
        `/drawer/v1/admin/drawer/structure/ui-component/${id}`
      );

      getCustomComponents();
      Toastify('success', 'UI Component deleted successfully');

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const desc = {
        message: buildSummary('UI component', componentName, 'deleted'),
        status: res.status,
        method: res.config?.method,
        url: res.config?.url,
        category: 'drawer',
        action: 'delete_ui_component',
        ui_component_id: id,
        ui_component_name: componentName,
        user_id: user?.id,
        user_name: user?.name,
        details: [
          { label: 'UI Component ID', value: String(id) },
          ...(componentName
            ? [{ label: 'Name', value: componentName }]
            : []),
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify('error', 'UI Component not deleted');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to delete UI component "${componentName || id}"`,
          status: err?.response?.status || 500,
          method: 'DELETE',
          url: `/drawer/v1/admin/drawer/structure/ui-component/${id}`,
          category: 'drawer',
          action: 'delete_ui_component',
          ui_component_id: id,
          ui_component_name: componentName,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleEdit = async () => {
    if (!currentId || isNaN(Number(currentId))) {
      Toastify(
        'error',
        'Invalid UI Component ID. Please select correct UI Component'
      );
      return;
    }

    if (!name || name.length === 0) {
      Toastify('error', 'Please enter correct fields');
      return;
    }

    try {
      const nextData = { name, description };
      const res = await apiClient.put(
        `/drawer/v1/admin/drawer/structure/ui-component/${currentId}`,
        nextData
      );

      const original = editOriginalRef.current || {};
      const changes = diffFields(original, nextData, UI_COMPONENT_FIELD_SPECS);

      Toastify('success', 'UI Component updated successfully');
      getCustomComponents();
      handleEditDialogClose();

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const desc = {
        message: buildSummary(
          'UI component',
          name || original.name,
          'updated',
          changes.length
        ),
        status: res.status,
        method: res.config?.method,
        url: res.config?.url,
        category: 'drawer',
        action: 'update_ui_component',
        ui_component_id: currentId,
        ui_component_name: name || original.name,
        user_id: user?.id,
        user_name: user?.name,
        changes,
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (err: any) {
      Toastify('error', 'UI Component not updated');
      console.error(err);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await actionLog?.actionLogTrack(
        JSON.stringify({
          message: `Failed to update UI component "${name || editOriginalRef.current?.name || currentId}"`,
          status: err?.response?.status || 500,
          method: 'PUT',
          url: `/drawer/v1/admin/drawer/structure/ui-component/${currentId}`,
          category: 'drawer',
          action: 'update_ui_component',
          ui_component_id: currentId,
          ui_component_name: name || editOriginalRef.current?.name,
          user_id: user?.id,
          user_name: user?.name,
        }),
        true
      );
    }
  };

  const handleSubmit = () => {
    handleAddChannel();
  };

  const handleEditOpen = (data: any) => {
    setCurrentId(data?.id);
    setName(data?.name || '');
    setDescription(data?.description || '');
    editOriginalRef.current = {
      name: data?.name || '',
      description: data?.description || '',
    };
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
    setName('');
    setDescription('');
    setCurrentId(null);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-end mb-2">
            <Info className="h-4 w-4 text-black" />
            <p className="ml-2 text-sm text-black">
              Please create a{" "}
              <a href="https://feedback.lift.newengen.com/" target="_blank" rel="noopener noreferrer" className="underline text-blue-500 hover:no-underline">ticket</a>
              {" "}for any additions or modifications
            </p>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            These are the UI components available for use in the drawer and sidebar structure.
          </p>
          <div className="flex-1 min-h-0 flex flex-col">
            <DataTable
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="100%"
              columns={[
                { key: 'name', header: 'Name', accessor: 'name', width: '200px', render: (_v: any, row: any) => <span className="font-medium">{row.name}</span> },
                { key: 'description', header: 'Description', accessor: (row: any) => row.description || '--', width: '300px', render: (_v: any, row: any) => <span className="text-gray-600">{row.description || '--'}</span> },
                { key: 'created_at', header: 'Created At', accessor: (row: any) => formatDate(row.created_at), width: '140px', render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.created_at)}</span> },
                { key: 'updated_at', header: 'Updated At', accessor: (row: any) => formatDate(row.updated_at), width: '140px', render: (_v: any, row: any) => <span className="text-gray-600">{formatDate(row.updated_at)}</span> },
              ] as TableColumn<any>[]}
              data={paginatedData}
              loading={loadData}
              emptyMessage="No UI components found."
              stickyHeader
              showActions={false}
              pagination={{
                currentPage: page + 1,
                totalPages: Math.max(1, totalPages),
                pageSize: rowsPerPage,
                totalItems: rowData.length,
                onPageChange: (p) => setPage(p - 1),
                onPageSizeChange: (size) => { setRowsPerPage(Number(size)); setPage(0); },
                pageSizeOptions: [10, 20, 50, 100],
              }}
            />
          </div>
        </div>
      </ThemeProvider>

      {/* Create Sheet */}
      <Sheet open={openDialog} onOpenChange={(open) => !open && handleDialogClose()}>
        <SheetContent className="flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Add New UI Component</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="ui-component-name">Name *</Label>
                <Input
                  id="ui-component-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ui-component-description">Description</Label>
                <Input
                  id="ui-component-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                />
              </div>
            </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button onClick={handleDialogClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Sheet */}
      <Sheet open={openEditDialog} onOpenChange={(open) => !open && handleEditDialogClose()}>
        <SheetContent className="flex flex-col h-full">
          <SheetHeader>
            <SheetTitle>Edit UI Component</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-ui-component-name">Name *</Label>
                <Input
                  id="edit-ui-component-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-ui-component-description">Description</Label>
                <Input
                  id="edit-ui-component-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                />
              </div>
            </div>
          </div>
          <SheetFooter className="flex-shrink-0 mt-auto border-t pt-4 gap-2">
            <Button onClick={handleEditDialogClose} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default UIComponents;
